// --- Our Modules --------------------------------------------------------------------------------
import type { Translation } from "./types";
// --- Logic --------------------------------------------------------------------------------------
const ar: Translation = {
    common: {
        loading: "جارِ التحميل…",
        retry: "إعادة المحاولة",
        somethingWrong: "حدث خطأ، حاول مرة أخرى",
        logout: "خروج",
        save: "حفظ",
        cancel: "إلغاء",
        more: "المزيد",
        close: "إغلاق",
        confirm: "تأكيد",
        done: "تم",
        copy: "نسخ",
        copied: "تم النسخ",
        typeToConfirm: "اكتب «{word}» للتأكيد",
        filters: "التصفية",
        search: "بحث",
        applyFilters: "تطبيق",
        clearFilters: "مسح التصفية",
        back: "رجوع",
        edit: "تعديل",
        remove: "حذف",
        add: "إضافة",
        notSet: "غير محدد",
        yes: "نعم",
        no: "لا",
        all: "الكل",
        pagination: "التنقل بين الصفحات",
        previousPage: "الصفحة السابقة",
        nextPage: "الصفحة التالية",
        pageCount: "{count} من {total}",
        perPage: "في الصفحة",
        comingSoon: "قيد الإنشاء",
    },
    auth: {
        title: "لوحة المشرفين",
        subtitle: "فريق لوزي كور فقط",
        phone: "رقم الجوال",
        phonePlaceholder: "5xxxxxxxx",
        password: "كلمة المرور",
        passwordPlaceholder: "••••••••",
        login: "دخول",
        phoneRequired: "أدخل رقم جوال صحيح",
        passwordRequired: "أدخل كلمة المرور",
        temporaryPassword: "كلمة المرور الحالية مؤقتة — يُفضّل تغييرها من صفحة الحساب.",
    },
    sideBar: {
        brand: "لوزي كور",
        kind: "لوحة المشرفين",
        home: "الرئيسية",
        organizations: "الشركات",
        subscriptions: "الاشتراكات",
        clients: "الأشخاص",
        enquiries: "الطلبات",
        support: "الدعم",
        admins: "المشرفون",
    },
    /** طلبات الحسابات القادمة من نموذج الموقع التعريفي. */
    enquiries: {
        title: "الطلبات",
        subtitle: "{new} جديد · {total} إجمالًا",
        tabs: { all: "الكل", new: "جديد" },
        statuses: { new: "جديد", read: "مقروء", done: "منتهي" },
        empty: "لا يوجد شيء هنا.",
        searchHint: "اسم الشركة أو الشخص أو الجوال",
        detailsTitle: "تفاصيل الطلب",
        moveStatus: "نقل الحالة",
        confirmStatus: "نقل هذا الطلب إلى ({status})؟",
        noMessage: "لم يكتب رسالة.",
        convert: "إنشاء شركة من الطلب",
        convertHint: "يفتح نموذج الشركة مملوءًا بما كتبه، ثم يُسند أول مالك لها.",
        call: "اتصال",
    },
    support: {
        title: "الدعم",
        subtitle: "{pending} بانتظارنا · {total} إجمالًا",
        openHint: "كل ما لم يُغلق بعد. الطلب الذي ينتظرنا هو الرقم الوحيد هنا الذي عليه موعد.",
        tabs: { open: "مفتوح", pending: "بانتظارنا", contacted: "قيد المعالجة", solved: "تم الحل", all: "الكل" },
        statuses: { pending: "بانتظارنا", contacted: "قيد المعالجة", solved: "تم الحل" },
        statTotal: "إجمالي الطلبات",
        answered: "تم الرد",
        empty: "لا يوجد شيء هنا.",
        searchHint: "الموضوع أو كلمة من الرسالة",
        detailsTitle: "تفاصيل الطلب",
        respond: "الرد",
        editReply: "تعديل الرد",
        openClient: "فتح صفحة مُرسل الطلب",
        moveStatus: "نقل الحالة",
        confirmStatus: "نقل هذا الطلب إلى ({status})؟",
        fields: { reply: "ردّك" },
        replyHint: "يقرأه في لوحته الخاصة. لا يوجد بريد ولا رسائل نصية، فما تكتبه هنا هو الرد كله.",
        unknownCompany: "منشأة غير معروفة",
        unknownPerson: "شخص غير معروف",
    },
    screens: {
        home: "الرئيسية",
        organizations: "الشركات",
        organization: "الشركة",
        subscriptions: "الاشتراكات",
        clients: "الأشخاص",
        client: "الشخص",
        support: "الدعم",
        admins: "المشرفون",
        profile: "حسابي",
        notFound: "الصفحة غير موجودة",
    },
    home: {
        greetingMorning: "صباح الخير",
        greetingAfternoon: "مساء الخير",
        greetingEvening: "مساء الخير",
        subtitle: "حالة النظام كله في شاشة واحدة",
        attentionTitle: "يحتاج إلى إجراء",
        attentionClear: "لا شيء ينتظرك اليوم",
        attentionPayments: "تحويل بانتظار التأكيد",
        attentionGrace: "اشتراك في فترة السماح",
        attentionEnding: "ينتهي قريبًا",
        attentionStorage: "مساحة تخزين ممتلئة",
        attentionStorageRequests: "طلبات مساحة",
        attentionTickets: "طلبات دعم",
        organizations: "شركة",
        activeSubscriptions: "اشتراك نشِط",
        clients: "شخص",
        storageUsed: "مساحة مستخدمة",
        newest: "أحدث الشركات",
        endingSoon: "ينتهي قريبًا",
        seeAll: "عرض الكل",
    },
    organizations: {
        title: "الشركات",
        subtitle: "{count} شركة",
        searchPlaceholder: "الاسم أو العنوان العام أو المدينة",
        addOrganization: "شركة جديدة",
        empty: "لا توجد شركات مطابقة",
        tabs: { all: "الكل", blocked: "المحجوبة" },
        recordTabs: { details: "البيانات", usage: "الاستخدام", subscription: "الاشتراك", people: "الفريق" },
        access: { free: "مجاني", full: "نشِط", readOnly: "قراءة فقط", blocked: "محجوبة", grace: "فترة سماح" },
        counts: { projects: "مشروع", units: "وحدة", customers: "عميل", clients: "موظف" },
        fields: {
            name: "اسم الشركة",
            slug: "العنوان العام",
            city: "المدينة",
            address: "العنوان",
            phone: "الجوال",
            email: "البريد",
            crNumber: "السجل التجاري",
            theme: "قالب المعرض",
            languages: "لغات النشر",
            createdAt: "أُنشئت في",
        },
        createTitle: "شركة جديدة",
        editTitle: "تعديل الشركة",
        nameRequired: "اسم الشركة مطلوب بلغة واحدة على الأقل",
        slugHint: "يصبح عنوان معرضها العام: {host}",
        slugInvalid: "العنوان العام يقبل حروفًا إنجليزية صغيرة وأرقامًا وشرطة واحدة بين الكلمات، ولا يبدأ أو ينتهي بشرطة.",
        slugFix: "صحّح العنوان العام قبل الحفظ.",
        storage: "التخزين",
        storageOf: "{used} من {limit}",
        openCatalog: "المعرض العام",
        block: "حجب الشركة",
        unblock: "إلغاء الحجب",
        confirmBlock: "ستصبح «{name}» غير قابلة للوصول لكل موظفيها، ويُغلق معرضها العام. بياناتها لا تُحذف.",
        confirmUnblock: "سيعود موظفو «{name}» إلى العمل، ويُفتح معرضها العام من جديد.",
        noSubscription: "لم تشترك هذه الشركة بعد، وتعمل داخل الحد المجاني.",
        freeQuota: "الحد المجاني: {projects} مشاريع · {units} وحدة · {storage}",
        endsIn: "ينتهي بعد {days} يومًا",
        lapsed: "انتهى قبل {days} يومًا",
        history: "الفترات السابقة",
        noPeople: "لا أحد يعمل في هذه الشركة بعد",
        owner: "المالك",
        setOwner: "تعيين المالك",
        ownerTitle: "مالك {name}",
        ownerHint:
            "تُنشأ الشركة فارغة، ولا يضيف بقية الفريق إلا مالكها — فهذا هو الحساب الوحيد الذي ننشئه لها. المالك واحد دائمًا، وبعد ذلك تنتقل الملكية بترقية المالك لأحد فريقه من شاشة الشركة نفسها.",
        ownerNameHint: "إن كان الرقم مسجلًا بالفعل، يتولى صاحبه الشركة ويحتفظ بكلمة مروره — ولا يُستخدم هذا الاسم.",
        peopleHint: "بقية الفريق يضيفهم المالك من شاشة فريق الشركة نفسها.",
    },
    subscriptions: {
        title: "الاشتراكات",
        subtitle: "{pending} بانتظار التأكيد · {total} فترة",
        searchPlaceholder: "اسم الشركة أو عنوانها العام",
        empty: "لا توجد اشتراكات هنا",
        emptyPending: "لا يوجد تحويل بانتظار التأكيد",
        emptyEnding: "كل الفترات الجارية أمامها أكثر من {days} يومًا",
        tabs: { pending: "بانتظار التحويل", storage: "طلبات المساحة", live: "الجارية", ending: "تنتهي قريبًا", history: "السجل" },
        periods: { quarterly: "ربع سنوي", annually: "سنوي" },
        statuses: {
            pending: "بانتظار التحويل",
            active: "جارية",
            coming: "قادمة",
            grace: "فترة سماح",
            ended: "منتهية",
            cancelled: "ملغاة",
        },
        fields: {
            organization: "الشركة",
            period: "الفترة",
            basePrice: "سعر الخطة",
            price: "المبلغ المستحق",
            amountReceived: "المبلغ المحوّل",
            addons: "مساحة إضافية",
            paymentDueAt: "آخر موعد للتحويل",
            startsAt: "تبدأ في",
            endsAt: "تنتهي في",
            paidAt: "تاريخ التحويل",
            receipt: "مرجع الإيصال",
            note: "ملاحظة",
        },
        currency: "ريال",
        recordSubscription: "تسجيل اشتراك",
        createTitle: "تسجيل اشتراك",
        renewTitle: "تجديد لفترة قادمة",
        priceHint: "اتركه فارغًا ليُسجَّل بـ {amount} ريال — محسوبة من سعر الخطة والمساحات الإضافية — أو اكتب المبلغ المتفق عليه إن كان هناك خصم.",
        addonsHint: "كل إضافة {gb} غيغا بـ{price} {currency}، بسعر كامل وتُحمل إلى الفترة التالية.",
        confirmPayment: "تأكيد السداد",
        confirmTitle: "تأكيد سداد {name}",
        confirmHint: "تبدأ الفترة من تاريخ التحويل، أو بعد الفترة الجارية إن كانت الشركة مشتركة بالفعل.",
        receiptHint: "رقم العملية أو أي مرجع يعيدك إلى التحويل في كشف الحساب.",
        cancelRequest: "إلغاء الطلب",
        confirmCancelRequest: "سيُلغى الطلب الخاص بـ «{name}». يمكن تسجيل طلب جديد في أي وقت.",
        pendingHint: "الطلب الذي يمضي {days} يومًا دون تحويل يُلغى تلقائيًا في المهمة الليلية.",
        addStorage: "إضافة مساحة",
        storageTitle: "إضافة مساحة لـ {name}",
        storageHint: "تُضاف إلى الفترة الجارية وتتاح فورًا، ولا تُغيّر مبلغ الفترة المسجّل.",
        storageCount: "عدد الإضافات",
        pastPeriods: "الفترات السابقة",
        currentPeriod: "الفترة الحالية",
        addonOf: "مساحة إضافية ×{count}",
        addonWaiting: "طلب مساحة ×{count}",
        confirmStorage: "تأكيد تحويل المساحة",
        confirmStorageMessage: "تأكيد استلام {amount} ريال من {name} مقابل {count} حزمة ({gb} جيجابايت). تُضاف المساحة فورًا.",
        storageRequestHint: "طلبات مساحة من شركات لديها فترة جارية. المساحة لا تُضاف قبل تأكيد التحويل.",
        emptyStorage: "لا توجد طلبات مساحة بانتظار التحويل.",
        requestedBy: "طلبها {name}",
        dueOn: "آخر موعد {date}",
        noOrganizations: "لا توجد شركات بعد — أنشئ شركة أولًا.",
    },
    clients: {
        title: "الأشخاص",
        subtitle: "{count} شخصًا · الهوية هي رقم الجوال",
        searchPlaceholder: "الاسم أو رقم الجوال",
        empty: "لا يوجد أشخاص مطابقون",
        tabs: { all: "الكل", owners: "مُلّاك", multi: "في أكثر من شركة", temporary: "كلمة مرور مؤقتة", suspended: "موقوفون" },
        recordTabs: { memberships: "الشركات", details: "البيانات" },
        memberships: "الشركات التي يعمل بها",
        noMemberships: "لا ينتمي إلى أي شركة",
        inCompanies: "{count} شركة",
        editTitle: "تعديل البيانات",
        nameRequired: "الاسم مطلوب",
        notesHint: "لمن يأتي بعدك: ما هذا الحساب، ومن سأل عنه.",
        suspend: "إيقاف الحساب",
        activate: "تفعيل الحساب",
        confirmSuspend: "لن يستطيع «{name}» الدخول إلى أي شركة، وتُغلق كل أجهزته. بياناته وسجله لا تُحذف.",
        confirmActivate: "سيستطيع «{name}» الدخول مرة أخرى بكلمة المرور نفسها.",
        active: "مفعّل",
        suspended: "موقوف",
        fields: { name: "الاسم", phone: "الجوال", email: "البريد", notes: "ملاحظات", createdAt: "أُضيف في", status: "الحالة" },
    },
    admins: {
        title: "المشرفون",
        subtitle: "فريق لوزي كور · {count} مشرفين",
        addAdmin: "مشرف جديد",
        empty: "لا يوجد مشرفون مطابقون",
        superOnly: "هذه الشاشة للمشرف الأعلى فقط. بقية المشرفين لا يرونها في القائمة أصلًا.",
        super: "مشرف أعلى",
        active: "مفعّل",
        suspended: "موقوف",
        searchPlaceholder: "الاسم أو رقم الجوال",
        tabs: { all: "الكل", supers: "المشرفون الأعلى", suspended: "الموقوفون" },
        createTitle: "مشرف جديد",
        editTitle: "تعديل المشرف",
        fields: { name: "الاسم", phone: "رقم الجوال", email: "البريد", notes: "ملاحظات", isSuper: "مشرف أعلى", createdAt: "أُضيف في" },
        isSuperHint: "يصل إلى كل شيء، بما في ذلك إدارة المشرفين أنفسهم.",
        notesHint: "لمن يأتي بعدك: ما الغرض من هذا الحساب، ومن طلبه.",
        deactivate: "إيقاف الحساب",
        activate: "تفعيل الحساب",
        confirmDeactivate: "لن يستطيع «{name}» الدخول، وتُغلق كل أجهزته فورًا. حسابه وسجله لا يُحذفان.",
        confirmActivate: "سيعود «{name}» إلى الدخول بكلمة مروره السابقة.",
        lastSuperHint: "آخر مشرف أعلى مفعّل — لا يمكن إيقافه أو تخفيضه.",
        me: "أنت",
        nameRequired: "الاسم مطلوب",
        selfSuperHint: "لا يمكنك تغيير صلاحيتك بنفسك — مشرف أعلى آخر هو من يفعل ذلك.",
    },
    password: {
        issue: "كلمة مرور جديدة",
        title: "كلمة مرور جديدة لـ {name}",
        hint: "انسخها وسلّمها الآن — لن تظهر مرة أخرى، ويمكن لصاحبها تغييرها بعد الدخول.",
        confirmHint: "سيتم توليد كلمة مرور جديدة لـ «{name}»، وتتوقف كلمته الحالية عن العمل فورًا مع إغلاق كل أجهزته.",
        temporary: "كلمة مرور مؤقتة",
    },
    slugInfo: {
        title: "ما هو العنوان العام؟",
        definition: "هو العنوان الذي يفتح المعرض العام للشركة على الإنترنت.",
        exampleLabel: "مثال",
        exampleCaption: "هذا الجزء أنت من تختاره",
        changeNote: "يمكن تغييره لاحقًا، لكن تغييره ينقل المعرض إلى عنوان آخر — وكل رابط سبق أن شاركته الشركة يتوقف عن العمل.",
        rulesTitle: "القواعد",
        rules: [
            "حروف إنجليزية صغيرة وأرقام فقط",
            "شرطة ( - ) واحدة بين الكلمات",
            "لا يبدأ ولا ينتهي بشرطة",
            "بدون مسافات أو حروف عربية أو رموز مثل @ # !",
            "لا تتشارك شركتان عنوانًا واحدًا",
        ],
        button: "تمام",
    },
    roles: { owner: "مالك", manager: "مدير", member: "موظف" },
    pending: {
        hint: "هذا القسم لم يُبنَ بعد. لا توجد واجهة برمجية تغذّيه، ولن يُرسم شيء قبل أن توجد.",
        waiting: "بانتظار",
        navTitle: "لم يُبنَ بعد",
    },
    profile: {
        myProfile: "حسابي",
        changePassword: "تغيير كلمة المرور",
        currentPassword: "كلمة المرور الحالية",
        newPassword: "كلمة المرور الجديدة",
        confirmPassword: "تأكيد كلمة المرور الجديدة",
        passwordChanged: "تم تغيير كلمة المرور",
        passwordHint: "بعد التغيير ستُغلق كل الأجهزة الأخرى، ويبقى هذا الجهاز فقط.",
        passwordMismatch: "كلمتا المرور غير متطابقتين",
        passwordTooShort: "كلمة المرور لا تقل عن {count} خانات",
    },
};

export default ar;
